import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone, Briefcase, Heart, Car, AlertTriangle, Smartphone, CheckCircle } from 'lucide-react';
import { practiceAreas, testimonials, firmInfo } from '../mock';

const Home = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        padding: '60px 0'
      }}>
        {/* Background Image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <img
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxsYXd5ZXJ8ZW58MHx8fHwxNzY4NDk2NjI2fDA&ixlib=rb-4.1.0&q=85"
            alt="Justice"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }} />

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 28, 27, 0.95), rgba(26, 28, 27, 0.7))'
          }} />
        </div>

        {/* Hero Content */}
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '800px' }}>
            <h1 className="heading-1" style={{
              color: 'var(--brand-primary)',
              marginBottom: '24px',
              animation: 'fadeIn 0.8s ease-out'
            }}>
              Injured? We Fight For Your Rights
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-white)',
              marginBottom: '32px',
              animation: 'fadeIn 0.8s ease-out 0.2s backwards'
            }}>
              With years of experience in personal injury law, LG Law Office provides aggressive representation for workers' compensation, auto accidents, and injury cases throughout California.
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              animation: 'fadeIn 0.8s ease-out 0.4s backwards'
            }}>
              <Link to="/free-consultation" className="btn-primary">
                Get Free Consultation
              </Link>
              <a href={`tel:${firmInfo.phone}`} className="btn-secondary">
                <Phone size={20} style={{ marginRight: '8px' }} />
                Call {firmInfo.phone}
              </a>
            </div>

            {/* Trust Indicators */}
            <div style={{
              display: 'flex',
              gap: '32px',
              marginTop: '48px',
              flexWrap: 'wrap',
              animation: 'fadeIn 0.8s ease-out 0.6s backwards'
            }}>
              <div>
                <div style={{ color: 'var(--brand-primary)', fontSize: '2rem', fontWeight: '700' }}>20+</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Years Experience</div>
              </div>
              <div>
                <div style={{ color: 'var(--brand-primary)', fontSize: '2rem', fontWeight: '700' }}>2500+</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Cases Won</div>
              </div>
              <div>
                <div style={{ color: 'var(--brand-primary)', fontSize: '2rem', fontWeight: '700' }}>$35M+</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Recovered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
              Our Practice Areas
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Specialized legal representation for injury victims across California
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {practiceAreas.map((area, index) => {
              return (
                <div
                  key={area.id}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-light)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = 'var(--border-light)';
                  }}>

                  {/* Image */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    <img 
                      src={area.iconImage} 
                      alt={area.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                      onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    />
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to bottom, transparent 0%, rgba(26, 28, 27, 0.7) 100%)'
                    }} />
                  </div>
                  
                  {/* Content */}
                  <div style={{ padding: '24px' }}>
                    <h3 className="heading-4" style={{ color: 'var(--text-white)', marginBottom: '12px' }}>
                      {area.title}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: '1.6' }}>
                      {area.shortDescription}
                    </p>
                    <Link
                      to="/practice-areas"
                      style={{
                        color: 'var(--brand-primary)',
                        textDecoration: 'none',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>

                      Learn More
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
              Why Choose LG Law Office
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              We're committed to fighting for maximum compensation for our clients
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
            { title: 'No Win, No Fee', description: 'We only get paid when you win your case' },
            { title: 'Free Consultation', description: 'Get expert legal advice at no cost' },
            { title: 'Proven Track Record', description: 'Millions recovered for our clients' },
            { title: '24/7 Availability', description: 'We\'re here when you need us most' },
            { title: 'Personalized Service', description: 'Every case gets our full attention' },
            { title: 'Trial Experience', description: 'Ready to fight for you in court' },
            { title: 'Strong Negotiation Skills', description: 'We achieve favorable settlements efficiently' },
            { title: 'Cutting-Edge Technology', description: 'We use advanced legal tech to enhance case outcomes' }].
            map((item, index) =>
            <div
              key={index}
              style={{
                padding: '32px',
                textAlign: 'center',
                animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>

                <CheckCircle size={48} color="var(--brand-primary)" style={{ marginBottom: '16px', flexShrink: 0 }} />
                <h3 className="heading-5" style={{ color: 'var(--text-white)', marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  {item.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
              What Our Clients Say
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Real testimonials from real clients we've helped
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {testimonials.map((testimonial, index) =>
            <div
              key={testimonial.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-light)',
                borderRadius: '16px',
                padding: '32px',
                animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
              }}>

                <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                  {[...Array(testimonial.rating)].map((_, i) =>
                <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                )}
                </div>
                <p style={{ color: 'var(--text-white)', marginBottom: '20px', lineHeight: '1.6', fontStyle: 'italic' }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <div style={{ color: 'var(--brand-primary)', fontWeight: '600' }}>{testimonial.name}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{testimonial.case}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 0',
        background: 'var(--bg-card)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'url(https://images.unsplash.com/photo-1765020553552-6286dde23660)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            Get Your Free Case Evaluation Today
          </h2>
          <p className="body-large" style={{ color: 'var(--text-white)', marginBottom: '40px', maxWidth: '700px', margin: '0 auto 40px' }}>
            Don't wait to get the compensation you deserve. Contact us now for a free, no-obligation consultation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/free-consultation" className="btn-primary" style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}>
              Schedule Free Consultation
            </Link>
            <a href={`tel:${firmInfo.phone}`} className="btn-secondary" style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}>
              <Phone size={20} style={{ marginRight: '8px' }} />
              {firmInfo.phone}
            </a>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;